import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { gl } from '../Engine';
import { RenderStorage } from '../core/render/RenderStorage';
import { QuadRenderer } from '../core/render/renderers/QuadRenderer';
import { FrameBufferObject } from '../core/utils/FrameBufferObject';
import { Environment } from '../data/Environment';
import { CustomObject } from '../data/common/customs/CustomObject';
import { View } from './View';
import { Layer, LayerBitMask, LayerClass } from './layers/Layer';
import { LayerBit } from './layers/LayerBit';

/**
 * Abstract class for all the {@link View Views} that use WebGL.\
 * {@link TextureView TextureViews} consist of {@link Layer Layers} and each {@link Layer} may be updated separately.\
 * {@link TextureView} also has it's own {@link WebGLTexture texture} that contains the view's last rendered frame.
 */
export abstract class TextureView
    extends View
    implements Iterable<Layer<unknown>>
{
    /**
     * Map of all the {@link Layer Layers} this {@link TextureView} contains.\
     *
     */
    protected layers: Map<LayerClass<unknown>, Layer<unknown>>;

    /**
     * {@link FrameBufferObject} that is used to update the contents of this {@link TextureView}.\
     * The {@link WebGL2RenderingContext.COLOR_ATTACHMENT0 COLOR_ATTACHMENT0} of that {@link FrameBufferObject} stores the texture data of this {@link TextureView}.
     */
    private readonly _frameBufferObject: FrameBufferObject;

    /**
     * The {@link ImageBitmapRenderingContext Bitmap renderer} that can be used to efficiently render a Bitmap\
     * generated on the {@link WebGL2RenderingContext WebGLContext} onto the {@link canvas}.
     */
    private bitmapRenderer?: ImageBitmapRenderingContext;

    /**
     * The constructor takes {@link HTMLCanvasElement} as an optional parameter.\
     * If {@link canvas}-parameter is defined, it will be used to also initialize the {@link frameBufferObject}.\
     * Otherwise the {@link frameBufferObject} will be initialized only once the {@link TextureView} gets a size.\
     * This constructor also populates the {@link layers}-map with {@link defaultLayers}.
     */
    public constructor(
        protected readonly env: Environment,
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement
    ) {
        super(canvas, canvas2D);
        this.env = env;
        this.layers = this.initializeDefaultLayers();
        if (canvas) {
            this.bitmapRenderer =
                canvas.getContext('bitmaprenderer') || undefined;
        }

        this._frameBufferObject = new FrameBufferObject(this.size);
    }

    /**
     * Method to update the size of the {@link View}, its {@link _frameBufferObject}, {@link Layer Layers} and their {@link Layer.filter Filters}.\
     * Once those have been updated, the texture contents are also updated to visualize the changes.
     * @param size The new size of this {@link TextureView} in pixels.
     * @returns Reference to this.
     */
    public resize(size: ReadonlyVec2, env = this.env): TextureView {
        if (this._frameBufferObject) {
            if (vec2.equals(this._frameBufferObject.size, size)) {
                //if the size equals the old size, do nothing.
                return this;
            }
            this._frameBufferObject.resize(size);
        }
        gl.viewport(
            0,
            0,
            this._frameBufferObject.width,
            this._frameBufferObject.height
        );
        for (const layer of this) {
            layer.resize(size, true, env);
        }
        this.updateLayers(true, env);
        return this;
    }

    protected onCanvasResize(): void {
        if (this.canvas) {
            this.resize(
                vec2.fromValues(
                    this.canvas.clientWidth,
                    this.canvas.clientHeight
                ),
                this.env
            );
        }
    }

    /**
     * Method that will initialize all the {@link defaultLayers} for this {@link TextureView view}.
     * @returns Map in which Key presents the {@link LayerClass} and Value holds the {@link Layer}-object
     */
    protected initializeDefaultLayers(): Map<
        LayerClass<unknown>,
        Layer<unknown>
    > {
        const map = new Map<LayerClass<unknown>, Layer<unknown>>();
        for (const layerClass of this.defaultLayers) {
            const layer = new layerClass(this, this.env);
            map.set(layerClass, layer);
        }
        return map;
    }

    /**
     * An abstract method to get an Array of {@link LayerClass LayerClasses}.\
     * These {@link LayerClass LayerClasses} define the types of the {@link Layer Layers}, that {@link TextureView Views} of this type should be constructed with.
     */
    public abstract get defaultLayers(): LayerClass<unknown>[];

    /**
     * Method to add a new {@link Layer} to this View.
     * New {@link Layer} is instantiated from the constructor defined by {@link LayerClass}.
     * @param layer {@link LayerClass} defining the type of {@link Layer} to be added.
     * @returns Reference to this
     */
    public addLayer(layer: LayerClass<unknown>): TextureView {
        const previous = this.layers.get(layer);
        if (previous) {
            //if Layer is being overwritten, it has to be deleted to deallocate possible WebGL memory.
            previous.delete();
        }
        this.layers.set(layer, new layer(this, this.env));
        return this;
    }

    /**
     * Method to get {@link Layer} of specified type from this {@link TextureView View}.
     * @param layer {@link LayerClass} defining the type of {@link Layer} we would like to get.
     * @returns A {@link Layer} of requested type if one exists, undefined otherwise.
     */
    public getLayer(layer: LayerClass<unknown>): Layer<unknown> | undefined {
        return this.layers.get(layer);
    }

    public get frameBufferObject(): FrameBufferObject | undefined {
        return this._frameBufferObject;
    }

    public get environment(): Environment {
        return this.env;
    }

    private readonly _customObjects: CustomObject[] = [];
    public addCustomObject(obj: CustomObject) {
        this._customObjects.push(obj);
    }

    public removeCustomObject(obj: CustomObject) {
        const index = this._customObjects.indexOf(obj);
        if (index >= 0) {
            const ref = this._customObjects[index];
            this._customObjects.splice(index, 1);
            ref.delete();
        }
    }

    /**
     * Method to render this {@link TextureView}'s {@link TextureView.layers Layers}.
     * @returns True to indicate that the {@link layers} were successfully {@link Layer.renderTexture rendered}.
     */
    public renderLayers() {
        if (!this.bitmapRenderer) {
            throw new Error(
                `Attempt to render Layers of TextureView that has no bitmapRenderer.`
            );
        } else if (!this._frameBufferObject) {
            throw new Error(
                `Attempt to render Layers of TextureView that has no FrameBufferObject`
            );
        }

        const offscreenCanvas = gl.canvas as OffscreenCanvas;

        if (
            offscreenCanvas.width < this.size[0] ||
            offscreenCanvas.height < this.size[1]
        ) {
            offscreenCanvas.width = this.size[0];
            offscreenCanvas.height = this.size[1];
        }

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        const renderer = RenderStorage.getRenderer(QuadRenderer);
        for (const layer of this.layers.values()) {
            layer.renderTexture(renderer);
        }
        for (const customObject of this._customObjects) {
            customObject.render(this);
        }
        gl.disable(gl.BLEND);

        createImageBitmap(
            offscreenCanvas,
            0,
            offscreenCanvas.height - this.height,
            this.width,
            this.height
        ).then((bitmap) => {
            this.bitmapRenderer?.transferFromImageBitmap(bitmap);
        });
    }

    public renderImageBitmap(image: ImageBitmap): boolean {
        if (this.bitmapRenderer) {
            this.bitmapRenderer.transferFromImageBitmap(image);
            return true;
        }
        return false;
    }

    public updateView(layers: LayerBitMask = LayerBit.ALL_LAYERS) {
        let contentUpdated = false;
        if (!this.prepareContentUpdate || this.prepareContentUpdate()) {
            contentUpdated = this.updateViewContent(layers);
            if (this.finishContentUpdate) {
                this.finishContentUpdate(contentUpdated);
            }
            return contentUpdated;
        }
    }

    public updateViewContent(layers: LayerBitMask): boolean {
        for (const layer of this) {
            if ((layer.layerBit & layers) == layer.layerBit) {
                layer.update(true, this.environment);
            }
        }
        return true;
    }

    /**
     * Method to update the {@link Layer.texture contents} of the {@link layers Layers} held within the {@link TextureView}.
     * @param updateContent Boolean flag controlling whether the contents of the {@link layers} should be updated (true).\
     * Or if the update only affects the post-processing (false).
     */
    protected updateLayers(updateContent: boolean, env = this.env): boolean {
        if (!this._frameBufferObject) {
            return false;
        }
        let updatedLayers = 0;
        this._frameBufferObject.bind();
        gl.viewport(
            0,
            0,
            this._frameBufferObject.width,
            this._frameBufferObject.height
        );
        for (const layer of this) {
            if (layer.update(updateContent, env)) {
                ++updatedLayers;
            }
        }
        this._frameBufferObject.unbind();
        if (updatedLayers > 0) {
            this.renderLayers();
        }
        return updatedLayers > 0;
    }

    /**
     * Method to prepare this {@link TextureView} for rendering.\
     * This will bind the {@link _frameBufferObject FrameBufferObject} and set the {@link gl.viewport viewport}.
     * @returns True to indicate that the {@link _frameBufferObject FrameBufferObject} was successfully bound and viewport set.
     */
    public prepareContentUpdate(): boolean {
        if (!this._frameBufferObject) {
            throw new Error(
                `Attempt to update TextureView that does not have FrameBufferObject`
            );
        }
        this._frameBufferObject.bind();
        gl.viewport(0, 0, this.width, this.height);
        return true;
    }

    /**
     * Method to inform this {@link TextureView} about content update's finish.\
     * This will unbind the {@link _frameBufferObject FrameBufferObject} and\
     * if contentUpdated is true, render the layers on the {@link canvas}.
     * @param contentUpdated Boolean flag indicating that the contents were changed and {@link TextureView View}'s {@link canvas} should be updated.
     */
    public finishContentUpdate(contentUpdated: boolean): void {
        if (!this._frameBufferObject) {
            throw new Error(
                `Attempt to finishContentUpdate of TextureView that does not have FrameBufferObject`
            );
        }
        this._frameBufferObject.unbind();
        if (contentUpdated) {
            this.renderLayers();
        }
    }

    public get width(): number {
        return this.size[0];
    }

    public get height(): number {
        return this.size[1];
    }

    public delete(): void {
        super.delete();
        if (this._frameBufferObject) {
            this._frameBufferObject.delete();
        }
        for (const layer of this) {
            layer.delete();
        }
    }

    [Symbol.iterator](): Iterator<Layer<unknown>, Layer<unknown>, undefined> {
        return this.layers
            ? (this.layers.values() as Iterator<
                  Layer<unknown>,
                  Layer<unknown>,
                  undefined
              >)
            : ([][Symbol.iterator]() as Iterator<
                  Layer<unknown>,
                  Layer<unknown>,
                  undefined
              >);
    }
}
